export { DEFAULT_WORKING_DAYS } from '@/utils/attendanceUtils';

export const settingsNavKeys = {
  company: 'company',
  teamSettings: 'teamSettings',
  appsAndUrls: 'appsAndUrls',
  workingHours: 'workingHours',
  workingDays: 'workingDays',
  screenshotsMonitoring: 'screenshotsMonitoring',
  alertsNotifications: 'alertsNotifications',
  leaveAttendance: 'leaveAttendance',
  holidays: 'holidays',
  privacySecurity: 'privacySecurity',
  advanced: 'advanced',
};

// icon/iconBg are resolved in the components that render these (IconBadge
// takes a react-icons element + a colors.scss variable name), kept out of
// this static config so the config stays framework-agnostic JSON-like data.
// Only nav items with a fully wired section are listed here - the rest are
// commented out (not deleted) until their sections are built/wired, so they
// don't show a dead/unsaveable "coming soon" page in the sidebar.
export const SETTINGS_NAV_ITEMS = [
  {
    key: settingsNavKeys.company,
    title: 'Company',
    subtitle: 'General & basic settings',
  },
  {
    key: settingsNavKeys.teamSettings,
    title: 'Team Settings',
    subtitle: 'Manage teams & preferences',
  },
  {
    key: settingsNavKeys.appsAndUrls,
    title: 'Apps & URLs',
    subtitle: 'Manage productive & distraction apps and URLs',
  },
  // {
  //   key: settingsNavKeys.workingHours,
  //   title: 'Working Hours',
  //   subtitle: 'Daily schedule & breaks',
  // },
  // {
  //   key: settingsNavKeys.workingDays,
  //   title: 'Working Days',
  //   subtitle: 'Set working days & weekends',
  // },
  // {
  //   key: settingsNavKeys.screenshotsMonitoring,
  //   title: 'Screenshots & Monitoring',
  //   subtitle: 'Capture & tracking preferences',
  // },
  {
    key: settingsNavKeys.alertsNotifications,
    title: 'Alerts & Notifications',
    subtitle: 'Manage alerts & channels',
  },
  {
    key: settingsNavKeys.leaveAttendance,
    title: 'Leave & Attendance',
    subtitle: 'Policies & attendance rules',
  },
  {
    key: settingsNavKeys.holidays,
    title: 'Holidays',
    subtitle: 'Manage your company holiday list',
  },
  // {
  //   key: settingsNavKeys.privacySecurity,
  //   title: 'Privacy & Security',
  //   subtitle: 'Data, privacy & compliance',
  // },
  // {
  //   key: settingsNavKeys.advanced,
  //   title: 'Advanced',
  //   subtitle: 'Advanced configurations',
  // },
];

export const DAYS_OF_WEEK = [
  { key: 'mon', short: 'Mon', long: 'Monday' },
  { key: 'tue', short: 'Tue', long: 'Tuesday' },
  { key: 'wed', short: 'Wed', long: 'Wednesday' },
  { key: 'thu', short: 'Thu', long: 'Thursday' },
  { key: 'fri', short: 'Fri', long: 'Friday' },
  { key: 'sat', short: 'Sat', long: 'Saturday' },
  { key: 'sun', short: 'Sun', long: 'Sunday' },
];

const buildTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (const minute of [0, 30]) {
      const period = hour < 12 ? 'AM' : 'PM';
      const displayHour = hour % 12 === 0 ? 12 : hour % 12;
      const label = `${String(displayHour).padStart(2, '0')}:${String(
        minute
      ).padStart(2, '0')} ${period}`;
      options.push({ label, value: label });
    }
  }
  return options;
};

export const TIME_OPTIONS = buildTimeOptions();

export const CAPTURE_FREQUENCY_OPTIONS = [
  { label: 'Every 1 minute', value: '1' },
  { label: 'Every 3 minutes', value: '3' },
  { label: 'Every 5 minutes', value: '5' },
  { label: 'Every 10 minutes', value: '10' },
  { label: 'Every 15 minutes', value: '15' },
];

export const SCREENSHOT_QUALITY_OPTIONS = [
  { label: 'Low (480p)', value: 'low' },
  { label: 'Medium (720p)', value: 'medium' },
  { label: 'High (1080p)', value: 'high' },
];

export const CAPTURE_DURING_OPTIONS = [
  { label: 'Working hours only', value: 'working-hours' },
  { label: 'All day', value: 'all-day' },
  { label: 'Custom hours', value: 'custom' },
];

export const screenshotTabKeys = {
  screenshotSettings: 'screenshotSettings',
  monitoringRules: 'monitoringRules',
  storageRetention: 'storageRetention',
  appPermissions: 'appPermissions',
};

export const SCREENSHOT_TABS = [
  { label: 'Screenshot Settings', value: screenshotTabKeys.screenshotSettings },
  { label: 'Monitoring Rules', value: screenshotTabKeys.monitoringRules },
  { label: 'Storage & Retention', value: screenshotTabKeys.storageRetention },
  { label: 'App & Permissions', value: screenshotTabKeys.appPermissions },
];

export const MONITORING_OPTIONS = [
  {
    key: 'activeWindow',
    title: 'Active Window',
    description: 'Track the active window title and application usage',
    iconBg: 'blue-100',
    iconColor: 'blue-600',
  },
  {
    key: 'keyboardActivity',
    title: 'Keyboard Activity',
    description: 'Track keyboard & mouse activity',
    iconBg: 'pink-100',
    iconColor: 'pink-600',
  },
  {
    key: 'webActivity',
    title: 'Web Activity',
    description: 'Monitor websites visited',
    iconBg: 'success-100',
    iconColor: 'success-600',
  },
  {
    key: 'applicationUsage',
    title: 'Application Usage',
    description: 'Track time spent on applications',
    iconBg: 'indigo-100',
    iconColor: 'indigo-600',
  },
  {
    key: 'idleTime',
    title: 'Idle Time',
    description: 'Detect and track idle time',
    iconBg: 'purple-100',
    iconColor: 'purple-600',
  },
];

export const DEFAULT_MONITORING_OPTIONS = MONITORING_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.key]: true }),
  {}
);

export const QUICK_SETTINGS_CARDS = [
  {
    key: settingsNavKeys.screenshotsMonitoring,
    title: 'Screenshots & Monitoring',
    description: 'Control screenshot capture frequency and monitoring.',
    iconBg: 'blue-100',
    iconColor: 'blue-600',
    navKey: null,
  },
  {
    key: 'stealthMode',
    title: 'Stealth Mode',
    description: 'Hide the application from user activity and app switching.',
    iconBg: 'rose-100',
    iconColor: 'rose-600',
    navKey: null,
  },
  {
    key: settingsNavKeys.alertsNotifications,
    title: 'Alerts & Notifications',
    description: 'Configure productivity alerts, thresholds and notifications.',
    iconBg: 'pink-100',
    iconColor: 'pink-600',
    navKey: settingsNavKeys.alertsNotifications,
  },
  {
    key: settingsNavKeys.leaveAttendance,
    title: 'Leave & Attendance',
    description: 'Manage leave policies, allocation and attendance settings.',
    iconBg: 'success-100',
    iconColor: 'success-600',
    navKey: settingsNavKeys.leaveAttendance,
  },
  // Not built/wired yet - hidden from the Company page's quick-settings grid
  // until they have a real section to link to.
  // {
  //   key: settingsNavKeys.privacySecurity,
  //   title: 'Privacy & Security',
  //   description: 'Manage data privacy, access controls and compliance.',
  //   iconBg: 'blue-100',
  //   iconColor: 'blue-600',
  //   navKey: null,
  // },
  // {
  //   key: settingsNavKeys.advanced,
  //   title: 'Advanced',
  //   description: 'Advanced configurations and system preferences.',
  //   iconBg: 'blue-gray-100',
  //   iconColor: 'blue-gray-600',
  //   navKey: null,
  // },
];
